import { createStore, combineReducers } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension';
import { ConfigurationInputsUI} from '../TypescriptClient';
import { IAction, IDesignRequestDetails, IFactoryRequest, IModifications, IModifyPropertyError, INotification, IReleaseRequestDetails, ISubmittalRequestDetails } from '../interfaces/interfaces';
import {reduxProjectManagement} from './reducers/projectManagement';
import { reduxUnitCabinet } from './reducers/unitCabinet';
import { getInitialState } from '../redux/localstorage';
import {reduxOptimizeDesign} from './reducers/optimizeDesign';

//GLOBAL REDUCER: This is a special reducer that fires on every redux update
//It updates a timestamp to keep track of user session activity.
const reduxSession = (state = { userActivityTimestamp: Date.now() }, action: IAction) => {
  const d = new Date();
  return Object.assign({}, state, { userActivityTimestamp: d });
};

const reduxAppVersion = (state = { appVersion: '' }, action: IAction) => {
  if (action.type === 'UPDATE_APP_VERSION') {
    return Object.assign({}, state, action.newState);
  }
  return state;
};

const reduxAuth = (state = { userId: '', access_token: '', userRoles:[], state: '', postalCode: '', userFirstName:'', userLastName: '', userType: ''}, action: IAction) => {
  if (action.type === 'UPDATE_AUTH') {
    return Object.assign({}, state, action.newState);
  }
  return state;
};

const reduxConfigInputs = (state: ConfigurationInputsUI = {}, action: IAction) => {
  if (action.type === 'UPDATE_CONFIG_INPUTS') {
    return action.newState ? Object.assign({}, state, action.newState) : {};
  }
  return state;
};

const reduxMenus = (
  state = {
    topMenu: { activeItem: 0 },
    sizingAccordion: { activeId: 'SAOutlet', drawingView: 'Top' },
    showModal: false,
    showTopRightDropdown1: false,
    showTopRightDropdown2: false,
    enableSave: true,
    enableUnitIcons:true,
    closeSetPointsAccordian: false,
    providedAndInstalledByChanged: false,
    optimizedDesignInputChanged: false,
    showPoolInputs: false
  },
  action: IAction
) => {
  if (action.type === 'UPDATE_MENUS') {
    return Object.assign({}, state, action.newState);
  }
  return state;
};

const reduxModals = (
  state = { showModal: false, modalType: '', modalId: '', modalData: null, hideCancelBtn: false, userResponse: null },
  action: IAction
) => {
  if (action.type === 'UPDATE_MODALS') {
    return Object.assign({}, state, action.newState);
  }
  return state;
};

const reduxOutputDocumentsInfo = (
  state={projectId: null, pdfRecords:null, activeProcessingRecordsCount: 0, pollForUpdates : false, showDocsReadyNotificationIcon: false, isOutputPage: false},
  action: IAction
) => {
  if(action.type === 'UPDATE_ACTIVEOUTPUTDOCUMENTS'){
    return Object.assign({}, state, action.newState);
  }
  return state;
};

const defaultErrors: Array<IModifyPropertyError> = [];
//NOTE on [userEditedFields]: keeps track of fields a user has edited at least once on the current page.
//Its just used to prevent initial field validation errors when you land on a screen the first time.
//This gets reset each time user goes to a new page.
const defaultStringArray: Array<string> = [];
const reduxValidation = (
  state = { modifyPropertyErrors: defaultErrors, userEditedFields: defaultStringArray },
  action: IAction
) => {
  if (action.type === 'UPDATE_VALIDATION') {
    return Object.assign({}, state, action.newState);
  }
  return state;
};

const reduxServerErrors = (
  state = { showError: false, errorType: null, errorSource: '', errorMessage: '' },
  action: IAction
) => {
  if (action.type === 'UPDATE_SERVER_ERROR') {
    return Object.assign({}, state, action.newState);
  }
  return state;
};

//Toast-like notifications that appear at the bottom of the screen
//Types: success, info, warning, error
const notificationsList: Array<INotification> = [];
const reduxNotifications = (state = { notifications: notificationsList }, action: IAction) => {
  if (action.type === 'UPDATE_NOTIFICATIONS') {
    return Object.assign({}, state, action.newState);
  }
  return state;
};

//Container for any special items we need to display
const reduxDisplay = (state = { showLoader: false, unitToDelete:"", headerTitleChanged:false, unitTag: "", projectName: ""}, action: IAction) => {
  if (action.type === 'UPDATE_DISPLAY') {
    return Object.assign({}, state, action.newState);
  }
  return state;
};

//Container for errors that prevent a page from being able to load
const reduxPrerequisiteErrors = (state = { errorList: null }, action: IAction) => {
  if (action.type === 'UPDATE_PREREQUISITE_ERRORS') {
    return Object.assign({}, state, action.newState);
  }
  return state;
};

//Container for pricing at top right of screen
const reduxPricing = (state = { priceNeedsUpdate: true, runPricing: false }, action: IAction) => {
  if (action.type === 'UPDATE_PRICING') {
    return Object.assign({}, state, action.newState);
  }
  return state;
};
const reduxComponentPerformance = (state: any = {}, action: IAction) => {
  if (action.type === 'UPDATE_COMPONENT_PERFORMANCE') {
    return action.newState ? Object.assign({}, state, action.newState) : {};
  }
  return state;
};
const factoryRequest:IFactoryRequest = {
  ProjectId: "",
  RequestType: "Design",
  CrmOpportunityId: "",
  SelectedUnits:[]
}
const designRequestDetails:IDesignRequestDetails = {
    Pricing:"",
    BasisOfDesign:"",
    BidDate:"",
    AdditionalEmailRecipients:[]
}  
const submittalRequestDetails:ISubmittalRequestDetails = {
    PurchaseOrderNumber:"",
    ConsultingEngineer:"",
    Contractor:"",
    AdditionalEmailRecipients:[]
  }
const releaseRequestDetails:IReleaseRequestDetails = {
    ShippingDateType:"",
    RequestedDeliveryDate:"",
    NotifyCustomerHours:0,
    WillAdviseShippingAddress:false,
    ContactName:"",
    ContactPhoneNumber:"",
    AddressLine1:"",
    AddressLine2:"",
    City:"",
    StateRegion:"",
    ZipPostalCode:"",
    CountryRegion:"",
    SpecialNotes:"",
    BillToAccount:"",
    PurchaseOrderNumber:"",
    IsTaxExempt:false,
    ConsultingEngineer:"",
    Contractor:"",
    ProjectType:"",
    BuildingType:"",
    ConstructionType:"",
    IsFinalInstallUS:true,
    GoodsUsedForWeapons:false,
    JobCountry:""
  }
const reduxFactoryRequest = (state: any = {factoryRequest}, action: IAction) : IFactoryRequest=> {
  if (action.type === 'UPDATE_FACTORY_REQUEST') {
    return Object.assign({}, state, action.newState);
  }
  return state;
};
const reduxFactoryDesignDetails = (state: any = {designRequestDetails}, action: IAction) : IDesignRequestDetails=> {
  if (action.type === 'UPDATE_DESIGN_DETAILS') {
    return Object.assign({}, state, action.newState);
  }
  return state;
};
const reduxFactorySubmittalDetails = (state: any = {submittalRequestDetails}, action: IAction) : ISubmittalRequestDetails=> {
  if (action.type === 'UPDATE_SUBMITTAL_DETAILS') {
    return Object.assign({}, state, action.newState);
  }
  return state;
};
const reduxFactoryReleaseDetails = (state: any = {releaseRequestDetails}, action: IAction) : IReleaseRequestDetails=> {
  if (action.type === 'UPDATE_RELEASE_DETAILS') {
    return Object.assign({}, state, action.newState);
  }
  return state;
};
const reduxFactoryModifications = (state: any = 
  {modifications: [
   { id: Date.now(), ModificationType: "Select Type", ModifiedUnits: [], Description: "" }
  ]
}, action: IAction) => {
  if (action.type === 'UPDATE_MODIFICATIONS') {
    return Object.assign({}, state, action.newState);
  }
  return state;
};
const reduxFactoryRequestLog = (state: any[] = [], action: IAction) => {
  if (action.type === 'UPDATE_FACTORY_REQUEST_LOG') {
    return action.newState;//Don't use Object.assign because state is an array, not an object.
  }
  return state;
};
const reduxFactoryRequestUnits = (state: any[] = [], action: IAction) => {
  if (action.type === 'UPDATE_FACTORY_REQUEST_UNITS') {
    return action.newState;//Don't use Object.assign because state is an array, not an object.
  }
  return state;
};
const reduxFactoryRequestBadUnits = (state: any[] = [], action:IAction) => {
  if (action.type === 'UPDATE_FACTORY_REQUEST_BAD_UNITS') {
    return action.newState;//Don't use Object.assign because state is an array, not an object.
  }
  return state;
}
export const store = createStore(
  combineReducers({
    reduxSession: reduxSession,
    reduxAppVersion: reduxAppVersion,
    reduxAuth: reduxAuth,
    reduxConfigInputs: reduxConfigInputs,
    reduxMenus: reduxMenus,
    reduxModals: reduxModals,
    reduxValidation: reduxValidation,
    reduxServerErrors: reduxServerErrors,
    reduxNotifications: reduxNotifications,
    reduxDisplay: reduxDisplay,
    reduxPrerequisiteErrors: reduxPrerequisiteErrors,
    reduxUnitCabinet: reduxUnitCabinet,
    reduxProjectManagement:reduxProjectManagement,
    reduxOptimizeDesign: reduxOptimizeDesign,
    reduxOutputDocumentsInfo: reduxOutputDocumentsInfo,
    reduxPricing: reduxPricing,
    reduxComponentPerformance: reduxComponentPerformance,
    reduxFactoryRequest: reduxFactoryRequest,
    reduxFactoryDesignDetails: reduxFactoryDesignDetails,
    reduxFactorySubmittalDetails: reduxFactorySubmittalDetails,
    reduxFactoryReleaseDetails: reduxFactoryReleaseDetails,
    reduxFactoryModifications: reduxFactoryModifications,
    reduxFactoryRequestLog: reduxFactoryRequestLog,
    reduxFactoryRequestUnits: reduxFactoryRequestUnits,
    reduxFactoryRequestBadUnits: reduxFactoryRequestBadUnits
  }),
  getInitialState(),
  composeWithDevTools()
);
